package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class Availability(
    val userId: String,
    val rosterId: String? = null,
    val workplaceId: String? = null,
    val month: String,
    val isLocked: Boolean,
    val isFilled: Boolean = false,
    val days: Map<Int, DayPreference>, // Key: Day of month (1..31)
    /** SH-173: given through the e-mailed link's web form (`source: "EMAIL_LINK"`), not in the app. */
    val viaEmailLink: Boolean = false
)

@Serializable
data class DayPreference(
    val status: AvailabilityStatus,
    val preferredSlotId: String?, // e.g. "SLOT_MORNING"
    val comment: String?,
    /** SH-135: the hours the person gave (empty: the whole day or not given), for display. */
    val hours: List<HoursRange> = emptyList()
)

@Serializable
enum class AvailabilityStatus { AVAILABLE, UNAVAILABLE }

@Serializable
data class AvailabilityRequest(
    val id: String,
    val workplaceId: String,
    val rosterId: String,
    val month: String,
    val period: AvailabilityPeriod,
    val dueDate: String, // ISO 8601
    val sentAt: String,  // ISO 8601
    val createdBy: String,
    val message: String?,
    /** SH-173: who also gets the request by e-mail, with a link to the web form. */
    val emailLinks: AvailabilityEmailLinks = AvailabilityEmailLinks.NONE
)

@Serializable
data class AvailabilityPeriod(
    val startDate: String,
    val endDate: String
)

@Serializable
data class AvailabilitySlot(
    val id: String, // "MORNING"
    val label: String, // "Poranek (6-14)"
    val startTime: String,
    val endTime: String
) {
    companion object {
        /** The stable id of the slot every new workplace starts with (SH-60). */
        const val ALL_DAY_ID = "SLOT_ALL_DAY"

        /**
         * The whole day, 00:00-24:00: a new workplace's only slot until the owner defines their
         * own. [label] is the owner's word for it ("Cały dzień", "All day"), stored as data like
         * every other slot label (see CreateWorkplaceUseCase).
         */
        fun allDay(label: String) = AvailabilitySlot(id = ALL_DAY_ID, label = label, startTime = "00:00", endTime = "24:00")
    }
}

@Serializable
data class WeeklyAvailabilityTemplate(
    val id: String,
    val name: String,
    val userId: String,
    val workplaceId: String,
    val days: Map<Int, DayPreference> // Key: Day of week (1..7, where 1 is Monday)
)
